import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import { registrationService } from '../services/registrationService';
import { reminderService } from '../services/reminderService';
import { collaboratorService } from '../services/collaboratorService';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import LoadingOverlay from '../components/LoadingOverlay';
import './EventDetails.css';

const urlSys = process.env.REACT_APP_SHARE_URL || 'http://localhost:3000';

const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const renderPreview = (text) => {
  const escaped = escapeHtml(text || '');
  return escaped
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/~(.*?)~/g, '<del>$1</del>')
    .replace(/```(.*?)```/g, '<code>$1</code>')
    .replace(/\n/g, '<br />');
};

// Função para calcular a data do lembrete
const calculateReminderDate = (eventDate, daysBefore) => {
  // Extrair apenas a parte da data (YYYY-MM-DD)
  const eventDateOnly = eventDate.split('T')[0];
  const eventDateTime = new Date(eventDateOnly + 'T12:00:00');
  const reminderDateTime = new Date(eventDateTime);
  reminderDateTime.setDate(eventDateTime.getDate() - daysBefore);
  return reminderDateTime;
};

const showConfirmation = (message, onConfirm) => {
  const ConfirmToast = ({ closeToast }) => (
    <div>
      <p style={{ marginBottom: '1rem' }}>{message}</p>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            closeToast();
          }}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          Não
        </button>
        <button
          onClick={() => {
            onConfirm();
            closeToast();
          }}
          style={{
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '4px',
            background: '#007bff',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Sim
        </button>
      </div>
    </div>
  );

  toast(<ConfirmToast />, {
    position: 'top-center',
    autoClose: false,
    closeButton: false,
    draggable: false,
    closeOnClick: false
  });
};

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('registrations');
  const [importing, setImporting] = useState(false);
  const [winnerModalOpen, setWinnerModalOpen] = useState(false);
  const [winnerData, setWinnerData] = useState(null);
  const [drawConfigModalOpen, setDrawConfigModalOpen] = useState(false);
  const [drawCount, setDrawCount] = useState(1);
  const [addCollaboratorModalOpen, setAddCollaboratorModalOpen] = useState(false);
  const [newCollaborator, setNewCollaborator] = useState({ name: '', email: '', role: 'editor' });
  const [tempPassword, setTempPassword] = useState(null);
  const [sendReminderModalOpen, setSendReminderModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [sendToType, setSendToType] = useState('registration'); // 'registration' ou 'custom'
  const [selectedRegistration, setSelectedRegistration] = useState('');
  const [customPhone, setCustomPhone] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const fileInputRef = useRef(null);

  // Verificar permissões do usuário
  const isOwner = !user?.isCollaborator;
  const isManager = user?.isCollaborator && user?.role === 'manager';
  const isEditor = user?.isCollaborator && user?.role === 'editor';
  const isViewer = user?.isCollaborator && user?.role === 'viewer';
  const canEdit = isOwner || isManager;
  const canManageRegistrations = isOwner || isManager || isEditor;
  const canManageReminders = isOwner || isManager || isEditor;

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [eventData, regData, remData, collabData] = await Promise.all([
        eventService.getById(id),
        registrationService.getByEventId(id),
        reminderService.getByEventId(id),
        collaboratorService.getByEventId(id).catch(() => ({ collaborators: [] }))
      ]);
      setEvent(eventData.event);
      setRegistrations(regData.registrations);
      setReminders(remData.reminders);
      setCollaborators(collabData.collaborators || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await registrationService.exportCSV(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inscricoes_${event.title}.csv`;
      a.click();
      toast.success('Exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const handleOpenDrawConfig = () => {
    setDrawCount(1);
    setDrawConfigModalOpen(true);
  };

  const handleDrawWinner = async () => {
    try {
      setDrawConfigModalOpen(false);
      const data = await registrationService.drawWinner(id, drawCount);
      console.log(data);
      if (data.winners && data.winners.length > 1) {
        toast.success(`🎉 ${data.winners.length} pessoas sorteadas!`);
      } else if (data.winners && data.winners.length === 1) {
        toast.success(`🎉 Vencedor: ${data.winners[0].name}`);
      }
      
      setWinnerData(data.winners);
      setWinnerModalOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao sortear');
    }
  };

  const handleDeleteRegistration = async (regId) => {
    showConfirmation('Tem certeza que deseja excluir esta inscrição?', async () => {
      try {
        await registrationService.delete(id, regId);
        toast.success('Inscrição removida');
        loadData();
      } catch (error) {
        toast.error('Erro ao remover');
      }
    });
  };

  const handleToggleAcceptMessages = async (regId) => {
    try {
      await registrationService.toggleAcceptMessages(id, regId);
      toast.success('Status de mensagens atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const copyShareLink = () => {
    const link = `${urlSys}/evento/${event.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const handleDeleteReminder = async (reminderId) => {
    showConfirmation('Tem certeza que deseja remover este lembrete?', async () => {
      try {
        await reminderService.delete(id, reminderId);
        toast.success('Lembrete removido');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Erro ao remover lembrete');
      }
    });
  };

  const handleDuplicateReminder = (reminder) => {
    navigate(`/events/${id}/reminders/new`, {
      state: {
        duplicate: true,
        daysBefore: reminder.days_before,
        message: reminder.message,
        imageUrl: reminder.image_url
      }
    });
  };

  const handleOpenSendReminderModal = (reminder) => {
    setSelectedReminder(reminder);
    setSendToType('registration');
    setSelectedRegistration('');
    setCustomPhone('');
    setSendReminderModalOpen(true);
  };

  const handleSendSingleReminder = async () => {
    if (!selectedReminder) return;

    let phone = '';
    if (sendToType === 'registration') {
      if (!selectedRegistration) {
        toast.error('Selecione um inscrito');
        return;
      }
      const registration = registrations.find(r => r.id === parseInt(selectedRegistration));
      phone = registration?.phone;
    } else {
      if (!customPhone) {
        toast.error('Informe um número de telefone');
        return;
      }
      phone = customPhone;
    }

    setSendingReminder(true);
    try {
      await reminderService.sendSingle(id, selectedReminder.id, phone);
      toast.success('Lembrete enviado com sucesso!');
      setSendReminderModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao enviar lembrete');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleImportXLSX = async (eventChange) => {
    const file = eventChange.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      toast.error('Envie um arquivo XLSX');
      eventChange.target.value = '';
      return;
    }

    setImporting(true);
    try {
      const { summary } = await registrationService.importXLSX(id, file);
      const details = [
        `${summary.imported} adicionados`,
        `${summary.duplicates} duplicados`,
        `${summary.invalid} inválidos`,
        summary.limitReached ? 'limite de vagas alcançado' : null
      ]
        .filter(Boolean)
        .join(' • ');

      toast.success(`Importação concluída: ${details}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao importar inscritos');
    } finally {
      setImporting(false);
      eventChange.target.value = '';
    }
  };

  const triggerFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    try {
      const response = await collaboratorService.create(id, newCollaborator);
      setTempPassword(response.collaborator.tempPassword);
      toast.success('Colaborador adicionado com sucesso!');
      setNewCollaborator({ name: '', email: '', role: 'editor' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao adicionar colaborador');
    }
  };

  const handleRemoveCollaborator = async (collaboratorId) => {
    showConfirmation('Tem certeza que deseja remover este colaborador?', async () => {
      try {
        await collaboratorService.remove(collaboratorId);
        toast.success('Colaborador removido');
        loadData();
      } catch (error) {
        toast.error('Erro ao remover colaborador');
      }
    });
  };

  const handleUpdateRole = async (collaboratorId, newRole) => {
    try {
      await collaboratorService.updateRole(collaboratorId, newRole);
      toast.success('Permissão atualizada');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar permissão');
    }
  };

  const handleResetPassword = async (collaboratorId, collaboratorName) => {
    showConfirmation(
      `Resetar a senha de ${collaboratorName}? Uma nova senha temporária será gerada.`,
      async () => {
        try {
          const response = await collaboratorService.resetPassword(collaboratorId);
          setTempPassword(response.tempPassword);
          setAddCollaboratorModalOpen(true);
          toast.success('Senha resetada com sucesso!');
        } catch (error) {
          toast.error(error.response?.data?.error || 'Erro ao resetar senha');
        }
      }
    );
  };

  const closeCollaboratorModal = () => {
    setAddCollaboratorModalOpen(false);
    setNewCollaborator({ name: '', email: '', role: 'editor' });
    setTempPassword(null);
  };

  const closeWinnerModal = () => {
    setWinnerModalOpen(false);
    setWinnerData(null);
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!event) return null;

  return (
    <div className="event-details">
      {sendingReminder && <LoadingOverlay message="Enviando lembrete..." />}
      <div className="container">
        <div className="event-details-header">
          <div>
            <h1>{event.title}</h1>
            <p>📅 {format(new Date(event.start_date), 'dd/MM/yyyy HH:mm')}</p>
            <p>📍 {event.location}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {canEdit && (
              <Link to={`/events/${id}/edit`} className="btn btn-outline">
                Editar
              </Link>
            )}
            <button onClick={copyShareLink} className="btn btn-primary">
              📋 Copiar Link
            </button>
          </div>
        </div>

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'registrations' ? 'active' : ''}`}
            onClick={() => setActiveTab('registrations')}
          >
            Inscrições ({registrations.length})
          </button>
          <button
            className={`tab ${activeTab === 'reminders' ? 'active' : ''}`}
            onClick={() => setActiveTab('reminders')}
          >
            Lembretes ({reminders.length})
          </button>
          {isOwner && (
            <button
              className={`tab ${activeTab === 'collaborators' ? 'active' : ''}`}
              onClick={() => setActiveTab('collaborators')}
            >
              Colaboradores ({collaborators.length})
            </button>
          )}
        </div>

        {activeTab === 'registrations' && (
          <div className="tab-content">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              {canManageRegistrations && (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button onClick={handleExportCSV} className="btn btn-success">
                    📤 Exportar CSV
                  </button>
                  <button onClick={handleOpenDrawConfig} className="btn btn-secondary">
                    🎲 Sortear
                  </button>
                  <button 
                    onClick={triggerFilePicker} 
                    className="btn btn-outline" 
                    disabled={importing}
                    title="Envie um arquivo XLSX com os campos: nome, telefone e email (opcional)"
                  >
                    {importing ? 'Importando...' : '📥 Importar XLSX'}
                  </button>
                  <a 
                    href="/exemplo.xlsx" 
                    download="modelo_importacao.xlsx"
                    className="btn btn-outline"
                    title="Baixar arquivo modelo com os campos: nome, telefone e email"
                  >
                    📄 Modelo XLSX
                  </a>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleImportXLSX}
                  />
                </div>
              )}
              
              <div style={{
                padding: '0.75rem 1.25rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                marginLeft: 'auto'
              }}>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: 'white',
                  letterSpacing: '0.5px'
                }}>
                  incrições:&nbsp;
                  {registrations.length}
                  {event.max_attendees && (
                    <span>
                      {' / '}
                      {parseInt(event.max_attendees) + (parseInt(event.safety_margin) || 0)}
                    </span>
                  )}
                </span>
              </div>
            </div>

{registrations.length === 0 ? (
              <p>Nenhuma inscrição ainda</p>
            ) : (() => {
              // Ordenar inscrições por nome alfabeticamente
              const sortedRegistrations = [...registrations].sort((a, b) => 
                a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
              );

              // Agrupar por letra inicial
              const groupedByLetter = sortedRegistrations.reduce((acc, reg) => {
                const firstLetter = reg.name.charAt(0).toUpperCase();
                if (!acc[firstLetter]) {
                  acc[firstLetter] = [];
                }
                acc[firstLetter].push(reg);
                return acc;
              }, {});

              const letters = Object.keys(groupedByLetter).sort();

              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Telefone</th>
                      <th>Email</th>
                      <th>WhatsApp</th>
                      <th>Data</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {letters.map((letter) => (
                      <React.Fragment key={letter}>
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                          <td colSpan="6" style={{ fontWeight: 'bold', fontSize: '1.1rem', padding: '0.75rem' }}>
                            {letter}
                          </td>
                        </tr>
                        {groupedByLetter[letter].map((reg) => (
                          <tr key={reg.id}>
                            <td>{reg.name}</td>
                            <td>{reg.phone}</td>
                            <td>{reg.email || '-'}</td>
                            <td>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: canManageRegistrations ? 'pointer' : 'default', gap: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  checked={reg.accept_messages}
                                  onChange={() => canManageRegistrations && handleToggleAcceptMessages(reg.id)}
                                  disabled={!canManageRegistrations}
                                  style={{ cursor: canManageRegistrations ? 'pointer' : 'not-allowed', width: '18px', height: '18px' }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>
                                  {reg.accept_messages ? '✅' : '❌'}
                                </span>
                              </label>
                            </td>
                            <td>{format(new Date(reg.created_at), 'dd/MM/yyyy')}</td>
                            <td>
                              {canManageRegistrations && (
                                <button
                                  onClick={() => handleDeleteRegistration(reg.id)}
                                  className="btn btn-sm btn-danger"
                                >
                                  Excluir
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        )}

        {activeTab === 'reminders' && (
          <div className="tab-content">
            {canManageReminders && (
              <Link to={`/events/${id}/reminders/new`} className="btn btn-primary">
                + Novo Lembrete
              </Link>
            )}
            {reminders.length === 0 ? (
              <p style={{ marginTop: '1rem' }}>Nenhum lembrete cadastrado</p>
            ) : (
              <div style={{ marginTop: '1rem' }}>
                {reminders.map((reminder) => {
                  const reminderDate = calculateReminderDate(event.start_date, reminder.days_before);
                  const formattedDate = reminderDate.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  });
                  const creatorName = reminder.collaborator_name || reminder.user_name || 'Sistema';
                  
                  return (
                    <div key={reminder.id} className="card" style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <h3 style={{ margin: 0 }}>{reminder.days_before} dias antes</h3>
                        {reminder.image_url && (
                          <img 
                            src={`${process.env.REACT_APP_API_URL.replace(/\/api$/, '') || 'http://localhost:5000'}${reminder.image_url}`} 
                            alt="Prévia"
                            style={{ 
                              width: '60px', 
                              height: '60px', 
                              objectFit: 'cover', 
                              borderRadius: '4px',
                              border: '1px solid #ddd'
                            }}
                          />
                        )}
                      </div>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                        👤 Criado por: <strong>{creatorName}</strong>
                      </p>
                      <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', marginTop: '0.25rem', marginBottom: '1rem' }}>
                        📅 Será enviado em: <strong>{formattedDate}</strong>
                      </p>
                      <div
                        className="reminder-message"
                        dangerouslySetInnerHTML={{ __html: renderPreview(reminder.message) }}
                      />
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      {reminder.sent ? '✓ Enviado' : '⏳ Pendente'}
                    </p>
                    <div className="reminder-actions-row">
                      {!reminder.sent && canManageReminders && (
                        <>
                          <Link
                            to={`/events/${id}/reminders/${reminder.id}/edit`}
                            className="btn btn-sm btn-outline"
                          >
                            Editar
                          </Link>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteReminder(reminder.id)}
                          >
                            Excluir
                          </button>
                        </>
                      )}
                      {canManageReminders && (
                        <>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleDuplicateReminder(reminder)}
                            title="Criar novo lembrete com base neste"
                          >
                            📋 Duplicar
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleOpenSendReminderModal(reminder)}
                            title="Enviar para um inscrito ou número específico"
                          >
                            📤 Enviar Avulso
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'collaborators' && (
          <div className="tab-content">
            <button 
              onClick={() => setAddCollaboratorModalOpen(true)} 
              className="btn btn-primary"
            >
              + Adicionar Colaborador
            </button>
            
            {collaborators.length === 0 ? (
              <p style={{ marginTop: '1rem' }}>Nenhum colaborador cadastrado</p>
            ) : (
              <table className="table" style={{ marginTop: '1rem' }}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Permissão</th>
                    <th>Cadastrado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {collaborators.map((collab) => (
                    <tr key={collab.id}>
                      <td>{collab.name}</td>
                      <td>{collab.email}</td>
                      <td>
                        <select
                          value={collab.role}
                          onChange={(e) => handleUpdateRole(collab.id, e.target.value)}
                          className="form-select"
                        >
                          <option value="viewer">Visualizador</option>
                          <option value="editor">Editor</option>
                          <option value="manager">Gerente</option>
                        </select>
                      </td>
                      <td>{format(new Date(collab.created_at), 'dd/MM/yyyy')}</td>
                      <td>
                        <button
                          onClick={() => handleResetPassword(collab.id, collab.name)}
                          className="btn btn-sm btn-warning"
                          style={{ marginRight: '0.5rem' }}
                          title="Resetar senha"
                        >
                          Resetar Senha
                        </button>
                        <button
                          onClick={() => handleRemoveCollaborator(collab.id)}
                          className="btn btn-sm btn-danger"
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {addCollaboratorModalOpen && (
        <div className="modal-overlay" onClick={closeCollaboratorModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{tempPassword && !newCollaborator.name ? 'Senha Resetada' : 'Adicionar Colaborador'}</h2>
            {!tempPassword ? (
              <form onSubmit={handleAddCollaborator}>
                <div className="form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={newCollaborator.name}
                    onChange={(e) => setNewCollaborator({...newCollaborator, name: e.target.value})}
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newCollaborator.email}
                    onChange={(e) => setNewCollaborator({...newCollaborator, email: e.target.value})}
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Permissão</label>
                  <select
                    value={newCollaborator.role}
                    onChange={(e) => setNewCollaborator({...newCollaborator, role: e.target.value})}
                    className="form-control"
                  >
                    <option value="viewer">Visualizador - Apenas visualiza</option>
                    <option value="editor">Editor - Gerencia inscrições e lembretes</option>
                    <option value="manager">Gerente - Pode editar o evento</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={closeCollaboratorModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Adicionar
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <p>{tempPassword && !newCollaborator.name ? 'Senha resetada com sucesso!' : '✅ Colaborador adicionado com sucesso!'}</p>
                <p style={{ marginTop: '1rem' }}>
                  <strong>Senha temporária:</strong>
                </p>
                <div style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '1.25rem',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}>
                  {tempPassword}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginTop: '1rem' }}>
                  ⚠️ Envie esta senha para o colaborador. <span style={{ fontStyle: 'italic', color: 'var(--warning-color)' }}>(por WhatsApp, SMS ou outro meio seguro)</span> Ele será solicitado a alterá-la no primeiro login.
                </p>
                <div className="modal-actions">
                  <button 
                    className="btn btn-outline" 
                    onClick={() => {
                      navigator.clipboard.writeText(tempPassword);
                      toast.success('Senha copiada!');
                    }}
                  >
                    📋 Copiar Senha
                  </button>
                  <button 
                    className="btn btn-outline" 
                    onClick={() => {
                      navigator.clipboard.writeText("https://eventoz.com.br/login");
                      toast.success('Link copiado!');
                    }}
                  >
                    📋 Copiar Link
                  </button>
                  <button className="btn btn-primary" onClick={closeCollaboratorModal}>
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {sendReminderModalOpen && selectedReminder && (
        <div className="modal-overlay" onClick={() => !sendingReminder && setSendReminderModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>📤 Enviar Lembrete Avulso</h2>
            <p style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
              Envie este lembrete para um inscrito específico ou um número personalizado
            </p>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="radio"
                  name="sendToType"
                  value="registration"
                  checked={sendToType === 'registration'}
                  onChange={(e) => setSendToType(e.target.value)}
                  disabled={sendingReminder}
                />
                <span>Selecionar inscrito</span>
              </label>
              
              {sendToType === 'registration' && (
                <select
                  className="form-input"
                  value={selectedRegistration}
                  onChange={(e) => setSelectedRegistration(e.target.value)}
                  style={{ marginTop: '0.5rem' }}
                  disabled={sendingReminder}
                >
                  <option value="">Selecione um inscrito...</option>
                  {registrations.map((reg) => (
                    <option key={reg.id} value={reg.id}>
                      {reg.name} - {reg.phone}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="radio"
                  name="sendToType"
                  value="custom"
                  checked={sendToType === 'custom'}
                  onChange={(e) => setSendToType(e.target.value)}
                  disabled={sendingReminder}
                />
                <span>Número personalizado</span>
              </label>
              
              {sendToType === 'custom' && (
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ex: 5511999999999"
                  value={customPhone}
                  onChange={(e) => setCustomPhone(e.target.value)}
                  style={{ marginTop: '0.5rem' }}
                  disabled={sendingReminder}
                />
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-outline" 
                onClick={() => setSendReminderModalOpen(false)}
                disabled={sendingReminder}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSendSingleReminder}
                disabled={sendingReminder}
              >
                {sendingReminder ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {drawConfigModalOpen && (
        <div className="modal-overlay" onClick={() => setDrawConfigModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎲 Configurar Sorteio</h2>
            <p style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
              Escolha quantas pessoas deseja sortear
            </p>
            <div className="form-group">
              <label>Número de sorteados</label>
              <input
                type="number"
                min="1"
                max={registrations.length}
                value={drawCount}
                onChange={(e) => setDrawCount(Math.max(1, Math.min(registrations.length, parseInt(e.target.value) || 1)))}
                className="form-input"
                style={{ fontSize: '1.1rem' }}
              />
              <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                Total de inscritos: {registrations.length}
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setDrawConfigModalOpen(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleDrawWinner}>
                🎲 Sortear
              </button>
            </div>
          </div>
        </div>
      )}

      {winnerModalOpen && winnerData && Array.isArray(winnerData) && (
        <div className="modal-overlay" onClick={closeWinnerModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎉 {winnerData.length === 1 ? 'Vencedor do sorteio' : `${winnerData.length} Sorteados`}</h2>
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '1rem' }}>
              {winnerData.map((winner, index) => (
                <div 
                  key={winner.id} 
                  style={{ 
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    backgroundColor: index === 0 ? '#fff9e6' : '#f8f9fa',
                    borderRadius: '8px',
                    border: index === 0 ? '2px solid #ffc107' : '1px solid #dee2e6'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold',
                      color: index === 0 ? '#ffc107' : '#6c757d',
                      minWidth: '40px',
                      textAlign: 'center'
                    }}>
                      {index === 0 ? '🏆' : `${index + 1}º`}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 0.25rem 0', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {winner.name}
                      </p>
                      <p style={{ margin: '0', fontSize: '0.9rem', color: 'var(--text-light)' }}>
                        📱 {winner.phone}
                      </p>
                      {winner.email && (
                        <p style={{ margin: '0', fontSize: '0.9rem', color: 'var(--text-light)' }}>
                          ✉️ {winner.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="modal-actions" style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={closeWinnerModal}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
